/**
 * ECharts 공통 테마 (Eco-FinTech)
 *
 * 차트 컴포넌트마다 중복되던 툴팁/축/그리드라인 스타일을 모은다.
 * 값은 styles/tokens.css 의 --ebc-* 토큰과 맞춘다.
 */

export const chartColors = {
    primary: "#0f766e",
    accent: "#14b8a6",
    positive: "#10b981",
    warn: "#f97316",
    danger: "#ef4444",

    /* 보조 시리즈(합계 bar, 예측값 등) */
    neutral: "#cbd5e1",
    neutralSoft: "#e2e8f0",

    text: "#1e293b",
    textSub: "#64748b",
    textMuted: "#94a3b8",

    axisLine: "rgba(148, 163, 184, 0.35)",
    splitLine: "rgba(148, 163, 184, 0.25)",
    divider: "rgba(148, 163, 184, 0.24)",

    /* 일별·추이 차트 (첨부 스와치: #2dd4bf → #0f766e) */
    barMintTop: "#2dd4bf",
    barMintMid: "#1ea597",
    barMintBottom: "#0f766e",
    barLatestTop: "#2dd4bf",
    barLatestBottom: "#0f766e",
    barPeakTop: "#fdba74",
    barPeakBottom: "#ea580c",
    lineBlue: "#3b82f6",
    saturday: "#3b82f6",
    saturdaySoft: "#60a5fa",
    weekend: "#f43f5e",
    weekendSoft: "#fb7185",
    weekendBand: "rgba(226, 232, 240, 0.7)",
    missingFill: "#f1f5f9",
    missingStripe: "rgba(148, 163, 184, 0.7)",
    barMissingTop: "#f8fafc",
    barMissingBottom: "#e2e8f0",
};

/* 5단계 팔레트 (진함 → 연함). tokens.css 의 --ebc-navy-1~5 / --ebc-gray-1~5 와 같은 값 */
export const navyScale = ["#13203a", "#2f6fae", "#4a93d8", "#8ab9e8", "#cfe2f7"];
export const grayScale = ["#64748b", "#7f8da1", "#9aa7b7", "#b5c0cc", "#d0d9e2"];

function hexToRgba(hex, alpha) {
    const h = hex.replace("#", "");
    const full = h.length === 3 ? h.replace(/./g, "$&$&") : h;
    const n = parseInt(full, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * 세로 막대용 그라데이션 (위 불투명 → 아래로 흐려짐)
 * @param {string} hex - 막대 기준색 (#rrggbb)
 * @param {number} bottomAlpha - 막대 바닥 불투명도
 * @returns {Object} ECharts linear gradient
 */
export function barGradient(hex, bottomAlpha = 0.25) {
    return {
        type: "linear",
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
            { offset: 0, color: hex },
            { offset: 1, color: hexToRgba(hex, bottomAlpha) },
        ],
    };
}

/**
 * Bento 컨셉에 맞춘 툴팁 스타일 (라운드 + 부드러운 그림자, 테두리 없음)
 *
 * padding 을 0 으로 두는 이유: 기존 formatter 가 HTML 안에서 자체 패딩을 갖고 있어
 * ECharts 기본 패딩이 더해지면 라운드 모서리에 여백이 겹쳐 보인다.
 *
 * @param {Object} overrides - 개별 차트에서 덮어쓸 옵션
 * @returns {Object} ECharts tooltip 옵션 조각
 */
export function tooltipStyle(overrides = {}) {
    return {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderColor: "transparent",
        borderWidth: 0,
        borderRadius: 12,
        padding: 0,
        extraCssText: "box-shadow: 0 8px 32px rgba(15, 23, 42, 0.08); backdrop-filter: blur(8px);",
        textStyle: {
            color: chartColors.text,
            fontSize: 11,
        },
        ...overrides,
    };
}

/**
 * 축 라벨 스타일
 * @param {Object} overrides
 * @returns {Object} ECharts axisLabel 옵션 조각
 */
export function axisLabelStyle(overrides = {}) {
    return {
        color: chartColors.textSub,
        fontSize: 10,
        ...overrides,
    };
}

/**
 * 축선 스타일 (거의 보이지 않는 헤어라인)
 * @returns {Object} ECharts axisLine 옵션 조각
 */
export function axisLineStyle() {
    return {
        lineStyle: {
            color: chartColors.axisLine,
        },
    };
}

/**
 * 그리드라인 스타일
 * 완전히 끄지 않고 점선 + 저투명으로 남겨 값 비교 가능성을 유지한다.
 * @param {boolean} show
 * @returns {Object} ECharts splitLine 옵션 조각
 */
export function splitLineStyle(show = true) {
    return {
        show,
        lineStyle: {
            type: "dashed",
            color: chartColors.splitLine,
        },
    };
}

/**
 * 범례 텍스트 스타일
 * @param {Object} overrides
 * @returns {Object} ECharts legend.textStyle 옵션 조각
 */
export function legendTextStyle(overrides = {}) {
    return {
        color: chartColors.textSub,
        fontSize: 10,
        ...overrides,
    };
}

/**
 * 툴팁 내부 HTML 조각 (formatter 안에서 색상 하드코딩을 없애기 위한 헬퍼)
 */
export const tooltipHtml = {
    title: (text) =>
        `<div style="padding: 8px 12px; border-bottom: 1px solid ${chartColors.divider}; font-weight: 700; font-size: 12px; color: ${chartColors.text};">${text}</div>`,
    bodyOpen: () => '<div style="padding: 8px 12px;">',
    bodyClose: () => "</div>",
    row: (marker, label, value) =>
        `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; font-size: 11px;">
            <span style="display: flex; align-items: center; color: ${chartColors.textSub};">${marker}<span style="margin-left: 4px;">${label}</span></span>
            <span style="font-weight: 700; color: ${chartColors.text}; margin-left: 16px;">${value}</span>
        </div>`,
    total: (label, value) =>
        `<div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px; padding-top: 8px; border-top: 1px solid ${chartColors.divider}; font-size: 11px;">
            <span style="color: ${chartColors.text}; font-weight: 700;">${label}</span>
            <span style="font-weight: 700; color: ${chartColors.text};">${value}</span>
        </div>`,
};
